import { X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '../lib/api-error';
import { type ClientCoinsData, useRedeemCoinsMutation } from '../store/coachApi';
import Modal from './Modal';

interface RedeemCoinsModalProps {
  clientId: string;
  maxBalance: number;
  onClose: () => void;
  onSuccess: (data: ClientCoinsData) => void;
}

const RedeemCoinsModal = ({ clientId, maxBalance, onClose, onSuccess }: RedeemCoinsModalProps) => {
  const { t } = useTranslation();
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [redeemCoins, { isLoading: loading }] = useRedeemCoinsMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = Number(amount);
    if (!parsed || parsed <= 0 || parsed > maxBalance) {
      return;
    }

    setError('');
    try {
      const data = await redeemCoins({
        clientId,
        amount: parsed,
        note: note.trim() || undefined,
      }).unwrap();
      onSuccess(data);
      onClose();
    } catch (err) {
      setError(
        err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
          ? err.message
          : getApiErrorMessage(err, 'Не удалось списать коины'),
      );
    }
  };

  return (
    <Modal onClose={onClose}>
      <div className="modal-header flex justify-between items-start gap-4">
        <h2 className="text-xl font-bold text-slate-900 tracking-tight pr-2">
          {t('Списать коины')}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="p-2 hover:bg-slate-100 rounded-xl transition-colors text-slate-400 hover:text-slate-600"
          aria-label={t('Отмена')}
        >
          <X size={20} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="section-label mb-1 block">
            {t('Сумма')}
          </label>
          <input
            type="number"
            min={1}
            max={maxBalance}
            className="input-field"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={`1 – ${maxBalance}`}
            required
          />
          <p className="text-xs text-slate-500 mt-1">
            {t('Доступно')}: {maxBalance}
          </p>
        </div>

        <div>
          <label className="section-label mb-1 block">
            {t('Примечание')}
          </label>
          <textarea
            className="input-field min-h-[80px] resize-y"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('За что списаны коины')}
            rows={3}
          />
        </div>

        {error && <p className="text-sm text-red-500 font-medium">{error}</p>}

        <div className="modal-actions flex flex-col-reverse sm:flex-row gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn-secondary flex-1">
            {t('Отмена')}
          </button>
          <button type="submit" disabled={loading} className="btn-primary flex-1 sm:flex-[2]">
            {loading ? t('Сохранение...') : t('Списать')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default RedeemCoinsModal;
